/* FILE: ./frontend/src/features/legal/TermsOfService.jsx */
import React, { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';

export default function TermsOfService() {
  const { t } = useTranslation(); 

  // ⚡ SEO FIX: Forces Google to hide this page from search results
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = "robots";
    meta.content = "noindex, follow";
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <div className="max-w-4xl mx-auto py-12 px-6 bg-white shadow-sm mt-8 rounded-xl min-h-screen">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('legal.tos_title', 'Terms of Service')}</h1>
      <p className="text-sm text-gray-500 mb-8">{t('legal.last_updated', 'Last Updated: May 2026')}</p>

      <div className="prose prose-blue max-w-none space-y-6 text-gray-700">
        
        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.t1_title', '1. Acceptance and Eligibility')}</h2>
          <p>
            <Trans i18nKey="legal.t1_desc">By accessing Tariffbeater ("the Platform"), you agree to these Terms. 
            You verify that you are a registered business entity in Canada and have the authority to bind that entity. 
            <strong>Consumer (B2C) use is strictly prohibited.</strong></Trans>
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.t2_title', '2. Venue Only')}</h2>
          <p>
            <Trans i18nKey="legal.t2_desc_full">Tariffbeater acts exclusively as a digital venue to facilitate communication between Buyers and Suppliers in the Core & Shell construction sector. 
            We do not manufacture, inspect, store, or distribute any products listed. 
            <strong> We make no claim or warranty regarding the quality, safety, or legality of items advertised. </strong>
            Any agreement or transaction is strictly between the Buyer and Supplier.</Trans>
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.t3_title', '3. Prohibited Conduct and Restricted Items')}</h2>
          <p>
            <Trans i18nKey="legal.t3_desc_1">This platform is strictly for civil-use <strong>Industrial, Commercial, and Institutional (ICI)</strong> trade. 
            Listing items restricted under the <strong>Controlled Goods Program (CGP)</strong> or the <strong>Export Control List</strong> is strictly forbidden.</Trans>
          </p>
          <p className="mt-2 font-semibold">{t('legal.t3_desc_2', 'You may not use Tariffbeater to:')}</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>{t('legal.t3_l1', 'Trade weapons, defense equipment, or munitions.')}</li>
            <li>{t('legal.t3_l2', 'Trade hazardous chemicals or restricted biological agents.')}</li>
            <li>{t('legal.t3_l3', 'Attempt to bypass government sanctions or embargoes.')}</li>
            <li>{t('legal.t3_l4', 'Submit fraudulent RFQs to manipulate competitor pricing data.')}</li>
          </ul>
          <p className="mt-2 text-red-600 font-medium border-l-4 border-red-600 pl-3">
            {t('legal.t3_desc_3', 'Violation of these rules will result in an immediate, permanent ban and potential reporting to federal authorities.')}
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.t4_title', '4. Platform Integrity, Security & Audits')}</h2>
          <p>
            <Trans i18nKey="legal.t4_desc">You authorize Tariffbeater Inc. to verify your business identity using third-party government APIs. 
            We reserve the right to audit your profile at any time. 
            Providing false information, or altering your business scope to prohibited categories after verification ("Bait and Switch"), constitutes a material breach of these terms.</Trans>
          </p>
          <p className="mt-3">
            <Trans i18nKey="legal.t4_bot_shield">We utilize third-party security services, including Cloudflare Turnstile, to protect our infrastructure from bots, spam, and unauthorized automated access. By interacting with our platform, you consent to these automated security checks. Attempting to bypass, disable, or deceive these security measures is a direct violation of these Terms and will result in immediate account termination.</Trans>
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.t5_title', '5. Fees and Payments')}</h2>
          <p>{t('legal.t5_desc', 'By subscribing to a paid tier or purchasing credits, you agree to the following terms:')}</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><Trans i18nKey="legal.t5_l1"><strong>B2B Transactions:</strong> All purchases are final and non-refundable.</Trans></li>
            <li><Trans i18nKey="legal.t5_l2"><strong>Subscriptions:</strong> Recurring subscriptions will bill automatically until canceled.</Trans></li>
            <li><Trans i18nKey="legal.t5_l3"><strong>Credits:</strong> RFQ reply credits hold no cash value outside the platform and are non-transferable.</Trans></li>
          </ul>
          <p className="mt-2"><Trans i18nKey="legal.t5_desc_2">All payments are processed securely through Helcim. We are not responsible for any banking fees or overdraft charges incurred due to automated billing.</Trans></p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.t6_title', '6. Data Immutability and Tax Compliance')}</h2>
          <p>
            <Trans i18nKey="legal.t6_desc">To assist users with Canadian tax (CRA) and audit compliance, Tariffbeater maintains an <strong>immutable record</strong> of all RFQs and Quotes.
            By using this service, you acknowledge that:</Trans>
          </p>
          <ul className="list-disc pl-5">
            <li>{t('legal.t6_l1', 'Quotes cannot be deleted once accepted by a Buyer.')}</li>
            <li>{t('legal.t6_l2', 'Account deletion does not erase historical transaction data linked to other active users.')}</li>
            <li>{t('legal.t6_l3', 'You are solely responsible for remitting appropriate GST/HST/PST based on the transaction jurisdiction.')}</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.t7_title', '7. Intellectual Property')}</h2>
          <p>
            <Trans i18nKey="legal.t7_desc">All software, algorithms (including the Trade Simulator), design, and content on the Platform are the exclusive property of Tariffbeater Inc. You may not scrape, reverse-engineer, or attempt to extract source code or compliance logic from the platform.</Trans>
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.t8_title', '8. Limitation of Liability')}</h2>
          <p>
            <Trans i18nKey="legal.t8_desc_full">To the maximum extent permitted by Canadian law, Tariffbeater Inc. is not liable for any direct, indirect, incidental, or consequential damages 
            arising from the use of our Trade Simulator, HS Code classifications, or supplier connections. 
            All compliance determinations (CFTA, CUSMA, local building codes) and financial estimates are preliminary. Verification of final landed costs and regulatory compliance is the sole responsibility of the user.</Trans>
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900">{t('legal.t9_title', '9. Governing Law')}</h2>
          <p>
            <Trans i18nKey="legal.t9_desc">These Terms shall be governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein. Any disputes arising out of these Terms shall be resolved in the courts of Ontario.</Trans>
          </p>
        </section>

      </div>
    </div>
  );
}